import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@heroui/react";
import { User } from "src/utils/interfaces/interfaces";
import { useTranslation } from "react-i18next";
import { FaChevronRight } from "src/icons";

export interface IMemberListProps {
  user: User;
  onBlockAction: Function;
  onEditAction: Function;
  onRemoveAction: Function;
  isLastItem?: boolean;
}

export function MemberList({
  user = {
    _id: "",
    firstName: "",
    lastName: "",
    type: "",
    email: "",
    phone: "",
    company: "",
    transportCompany: { name: "" },
  },
  onBlockAction = () => { },
  onEditAction = () => { },
  onRemoveAction = () => { },
  isLastItem = false,
}: IMemberListProps) {
  const [showAction, setShowAction] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    visibility: "hidden" | "visible";
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const memberName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A";
  const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || "M";

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setShowAction(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const gutter = 12;
    const fallbackWidth = 220;
    const menuRect = menuRef.current?.getBoundingClientRect();
    const menuWidth = menuRect?.width || fallbackWidth;
    const menuHeight = menuRect?.height || 170;
    const spaceBelow = window.innerHeight - buttonRect.bottom - gutter;
    const spaceAbove = buttonRect.top - gutter;
    const placeAbove = isLastItem || (spaceBelow < menuHeight && spaceAbove > spaceBelow);

    const left = Math.min(
      window.innerWidth - menuWidth - gutter,
      Math.max(gutter, buttonRect.right - menuWidth)
    );

    const top = placeAbove
      ? Math.max(gutter, buttonRect.top - menuHeight - gutter)
      : Math.min(window.innerHeight - menuHeight - gutter, buttonRect.bottom + gutter);

    setMenuPosition({
      top,
      left,
      visibility: menuRect ? "visible" : "hidden",
    });
  }, [isLastItem]);

  useEffect(() => {
    if (!showAction) return;
    updateMenuPosition();

    const handleViewportChange = () => updateMenuPosition();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [showAction, updateMenuPosition]);

  const { t } = useTranslation();

  return (
    <>
      <div
        className="grid grid-cols-12 gap-5 px-7 py-7 border-b last:border-b-0 items-center"
        style={{
          backgroundColor: "var(--bg-surface)",
          borderColor: "var(--border-light)",
        }}
        key={user._id}
      >
        <div className="col-span-3 grid grid-cols-7 gap-5 min-w-0">
          <div
            className="col-span-2 self-center h-14 w-14 rounded-full flex items-center justify-center shadow-xl text-sm font-semibold"
            style={{
              backgroundColor: "var(--color-primary-light)",
              color: "var(--color-primary)",
              border: "1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)",
            }}
          >
            {initials}
          </div>
          <div className="col-span-5 min-w-0 self-center">
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              {t("vendors_page.member_name")}
            </p>
            <p className="text-sm truncate" style={{ color: "var(--text-main)" }}>
              {memberName}
            </p>
          </div>
        </div>

        <div className="col-span-2 min-w-0">
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            {t("vendors_page.user_type")}
          </p>
          <p className="text-sm truncate" style={{ color: "var(--text-main)" }}>
            {user.type || "N/A"}
          </p>
        </div>

        <div className="col-span-2 min-w-0">
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            {t("vendors_page.organization")}
          </p>
          <p className="text-sm truncate" style={{ color: "var(--text-main)" }}>
            {user.company ? user.company?.name : "N/A"}
          </p>
        </div>

        <div className="col-span-2 min-w-0">
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            {t("vendors_page.email")}
          </p>
          <p className="text-sm truncate" style={{ color: "var(--text-main)" }}>
            {user.email || "N/A"}
          </p>
        </div>

        <div className="col-span-2 min-w-0">
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            {t("vendors_page.contact_no")}
          </p>
          <p className="text-sm truncate" style={{ color: "var(--text-main)" }}>
            {user.phone || "N/A"}
          </p>
        </div>

        <div className="col-span-1 flex items-center justify-end">
          <Button
            variant="light"
            size="sm"
            isIconOnly
            ref={buttonRef}
            onPress={() => {
              if (showAction) {
                setShowAction(false);
                return;
              }
              updateMenuPosition();
              setShowAction(true);
            }}
            onMouseDown={(e) => e.preventDefault()}
            className="rounded-full shadow-lg border transition-colors duration-200 outline-none focus:outline-none focus:ring-0 data-[focus-visible=true]:outline-none data-[focus-visible=true]:ring-0"
            style={{
              backgroundColor: "var(--bg-surface-2)",
              borderColor: "var(--border-light)",
              color: "var(--color-primary)",
              outline: "none",
              boxShadow: "none",
              WebkitTapHighlightColor: "transparent",
            }}
            startContent={<FaChevronRight className="rtl:rotate-180" />}
          />
        </div>
      </div>

      {showAction && menuPosition && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[9999] min-w-[220px] rounded-2xl border shadow-lg overflow-hidden"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            visibility: menuPosition.visibility,
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-light)",
            boxShadow: "0 16px 40px rgba(0,0,0,0.22)",
          }}
        >
          <div className="py-2">
            <button
              type="button"
              className="w-full px-5 py-3 text-left text-sm transition-colors duration-200"
              style={{ color: "var(--text-main)" }}
              onClick={() => {
                setShowAction(false);
                onEditAction(user.company ? user.company?.name : "N/A");
              }}
            >
              {t("vendors_page.edit.name")}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
